import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";

import { apiDebugLog, apiPost, isInvalidSessionError } from "../api";
import { useAuth } from "../auth/AuthProvider";

const AppDataContext = createContext(null);

const INITIAL_MAIN_PAGE_DATA = {
    expire: null,
};

const INITIAL_CABINET_DATA = {
    user_id: null,
    email: null,
    tg_id: null,
    ref_link: "",
    ref_type: "days",
    ref_percent: 0,
    ref_total: 0,
    ref_balance: 0,
    ref_added: 0,
    ref_active_added: 0,
    days_balance: 0,
};

const INITIAL_DEVICES_DATA = {
    devices_tariff_limit: 0,
    devices_info: [],
};

const INITIAL_TARIFFS_DATA = {
    curr_devices: 1,
    tarifs: {},
};

const INITIAL_INSTRUCTION_DATA = {
    downloadLinks: {},
    importLink: "",
};

const DEV_MAIN_PAGE_DATA = {
    expire: "31.12.2026",
};

const DEV_CABINET_DATA = {
    user_id: 1,
    email: null,
    tg_id: null,
    ref_link: "https://sodavpn.ru/?ref=dev",
    ref_type: "days",
    ref_percent: 15,
    ref_total: 1200,
    ref_balance: 450,
    ref_added: 8,
    ref_active_added: 6,
    days_balance: 12,
};

const DEV_DEVICES_DATA = {
    devices_tariff_limit: 3,
    devices_info: [
        {
            id: "dev-device-1",
            deviceModel: "Dev iPhone",
            platform: "iOS",
            osVersion: "18",
            createdAt: Date.now(),
        },
    ],
};

const DEV_TARIFFS_DATA = {
    curr_devices: 1,
    tarifs: {
        1: {
            "1m": { price: 150, per_month: 150, discount: 0 },
            "3m": { price: 420, per_month: 140, discount: 7 },
            "6m": { price: 780, per_month: 130, discount: 13 },
            "12m": { price: 1440, per_month: 120, discount: 20 },
        },
        2: {
            "1m": { price: 250, per_month: 250, discount: 0 },
            "3m": { price: 690, per_month: 230, discount: 8 },
            "6m": { price: 1260, per_month: 210, discount: 16 },
            "12m": { price: 2280, per_month: 190, discount: 24 },
        },
        3: {
            "1m": { price: 350, per_month: 350, discount: 0 },
            "3m": { price: 960, per_month: 320, discount: 9 },
            "6m": { price: 1740, per_month: 290, discount: 17 },
            "12m": { price: 3120, per_month: 260, discount: 26 },
        },
    },
};

const DEV_INSTRUCTION_DATA = {
    downloadLinks: {
        android: { btns: ["icon-download;Скачать Happ;https://example.com/android"] },
        ios: { btns: ["icon-download;Открыть App Store;https://example.com/ios"] },
        windows: { btns: ["icon-download;Скачать Happ;https://example.com/windows"] },
        macos: { btns: ["icon-download;Открыть App Store;https://example.com/macos"] },
        linux: { btns: ["icon-download;Скачать Happ;https://example.com/linux"] },
    },
    importLink: "happ://import/dev-config",
};

function summarizeImportLink(importLink) {
    const normalizedLink = typeof importLink === "string" ? importLink.trim() : "";
    const [scheme = ""] = normalizedLink.split("://", 1);

    return {
        has_import_link: Boolean(normalizedLink),
        import_link_length: normalizedLink.length,
        import_link_scheme: scheme || null,
        import_link_prefix: normalizedLink.slice(0, 48) || null,
        import_link_suffix: normalizedLink.slice(-24) || null,
    };
}

export function AppDataProvider({ children }) {
    const auth = useAuth();
    const requestIdRef = useRef(0);

    const [mainPageData, setMainPageData] = useState(INITIAL_MAIN_PAGE_DATA);
    const [cabinetData, setCabinetData] = useState(INITIAL_CABINET_DATA);
    const [devicesData, setDevicesData] = useState(INITIAL_DEVICES_DATA);
    const [tariffsData, setTariffsData] = useState(INITIAL_TARIFFS_DATA);
    const [instructionData, setInstructionData] = useState(INITIAL_INSTRUCTION_DATA);

    const [mainPageStatus, setMainPageStatus] = useState({ isLoading: false, error: "" });
    const [cabinetStatus, setCabinetStatus] = useState({ isLoading: false, error: "" });
    const [devicesStatus, setDevicesStatus] = useState({ isLoading: false, error: "" });
    const [tariffsStatus, setTariffsStatus] = useState({ isLoading: false, error: "" });
    const [instructionStatus, setInstructionStatus] = useState({ isLoading: false, error: "" });

    const resetData = useCallback(() => {
        setMainPageData(INITIAL_MAIN_PAGE_DATA);
        setCabinetData(INITIAL_CABINET_DATA);
        setDevicesData(INITIAL_DEVICES_DATA);
        setTariffsData(INITIAL_TARIFFS_DATA);
        setInstructionData(INITIAL_INSTRUCTION_DATA);
        setMainPageStatus({ isLoading: false, error: "" });
        setCabinetStatus({ isLoading: false, error: "" });
        setDevicesStatus({ isLoading: false, error: "" });
        setTariffsStatus({ isLoading: false, error: "" });
        setInstructionStatus({ isLoading: false, error: "" });
    }, []);

    const applyDevData = useCallback(() => {
        setMainPageData(DEV_MAIN_PAGE_DATA);
        setCabinetData(DEV_CABINET_DATA);
        setDevicesData(DEV_DEVICES_DATA);
        setTariffsData(DEV_TARIFFS_DATA);
        setInstructionData(DEV_INSTRUCTION_DATA);
        setMainPageStatus({ isLoading: false, error: "" });
        setCabinetStatus({ isLoading: false, error: "" });
        setDevicesStatus({ isLoading: false, error: "" });
        setTariffsStatus({ isLoading: false, error: "" });
        setInstructionStatus({ isLoading: false, error: "" });
    }, []);

    const handleInvalidSessionError = useCallback((error, currentRequestId) => {
        if (!isInvalidSessionError(error)) {
            return false;
        }

        if (requestIdRef.current === currentRequestId) {
            requestIdRef.current += 1;
            resetData();
            auth.logout();
        }

        return true;
    }, [auth, resetData]);

    const fetchMainPageData = useCallback(async (currentRequestId) => {
        setMainPageStatus({ isLoading: true, error: "" });

        try {
            const response = await apiPost("/cabinet/main_page_data", {}, auth);
            if (requestIdRef.current !== currentRequestId) {
                return null;
            }

            setMainPageData({
                ...INITIAL_MAIN_PAGE_DATA,
                ...response,
            });
            setMainPageStatus({ isLoading: false, error: "" });
            return response;
        } catch (error) {
            if (requestIdRef.current !== currentRequestId) {
                return null;
            }

            if (handleInvalidSessionError(error, currentRequestId)) {
                return null;
            }

            setMainPageStatus({
                isLoading: false,
                error: "Не удалось загрузить данные подписки",
            });
            return null;
        }
    }, [auth, handleInvalidSessionError]);

    const fetchCabinetData = useCallback(async (currentRequestId) => {
        setCabinetStatus({ isLoading: true, error: "" });

        try {
            const response = await apiPost("/cabinet/cabinet_page_data", {}, auth);
            if (requestIdRef.current !== currentRequestId) {
                return;
            }

            setCabinetData({
                ...INITIAL_CABINET_DATA,
                ...response,
            });
            setCabinetStatus({ isLoading: false, error: "" });
        } catch (error) {
            if (requestIdRef.current !== currentRequestId) {
                return;
            }

            if (handleInvalidSessionError(error, currentRequestId)) {
                return;
            }

            setCabinetStatus({
                isLoading: false,
                error: "Не удалось загрузить данные кабинета",
            });
        }
    }, [auth, handleInvalidSessionError]);

    const fetchDevicesData = useCallback(async (currentRequestId) => {
        setDevicesStatus({ isLoading: true, error: "" });

        try {
            const response = await apiPost("/cabinet/devices_page_data", {}, auth);
            if (requestIdRef.current !== currentRequestId) {
                return;
            }

            setDevicesData({
                devices_tariff_limit: Number(response.devices_tariff_limit) || 0,
                devices_info: Array.isArray(response.devices_info) ? response.devices_info : [],
            });
            setDevicesStatus({ isLoading: false, error: "" });
        } catch (error) {
            if (requestIdRef.current !== currentRequestId) {
                return;
            }

            if (handleInvalidSessionError(error, currentRequestId)) {
                return;
            }

            setDevicesStatus({
                isLoading: false,
                error: "Не удалось загрузить устройства",
            });
        }
    }, [auth, handleInvalidSessionError]);

    const fetchTariffsData = useCallback(async (currentRequestId) => {
        setTariffsStatus({ isLoading: true, error: "" });

        try {
            const response = await apiPost("/cabinet/tarifs_page_data", {}, auth);
            if (requestIdRef.current !== currentRequestId) {
                return;
            }

            setTariffsData({
                curr_devices: Number(response.curr_devices) || INITIAL_TARIFFS_DATA.curr_devices,
                tarifs: response.tarifs && typeof response.tarifs === "object" ? response.tarifs : {},
            });
            setTariffsStatus({ isLoading: false, error: "" });
        } catch (error) {
            if (requestIdRef.current !== currentRequestId) {
                return;
            }

            if (handleInvalidSessionError(error, currentRequestId)) {
                return;
            }

            setTariffsStatus({
                isLoading: false,
                error: "Не удалось загрузить тарифы",
            });
        }
    }, [auth, handleInvalidSessionError]);

    const fetchInstructionData = useCallback(async (currentRequestId) => {
        setInstructionStatus({ isLoading: true, error: "" });

        try {
            const response = await apiPost("/cabinet/instruction_page_data", {}, auth);
            if (requestIdRef.current !== currentRequestId) {
                return;
            }

            const importLink = response.importLink || "";
            const debugPayload = {
                request_id: currentRequestId,
                has_download_links: Boolean(
                    response.downloadLinks && typeof response.downloadLinks === "object",
                ),
                ...summarizeImportLink(importLink),
            };
            console.warn("instruction_page_data_loaded", debugPayload);
            void apiDebugLog("instruction_page_data_loaded", debugPayload);

            setInstructionData({
                downloadLinks: response.downloadLinks && typeof response.downloadLinks === "object"
                    ? response.downloadLinks
                    : {},
                importLink,
            });
            setInstructionStatus({ isLoading: false, error: "" });
        } catch (error) {
            if (requestIdRef.current !== currentRequestId) {
                return;
            }

            if (handleInvalidSessionError(error, currentRequestId)) {
                return;
            }

            const debugPayload = {
                request_id: currentRequestId,
                message: error instanceof Error ? error.message : String(error),
                status: typeof error?.status === "number" ? error.status : null,
            };
            console.error("instruction_page_data_failed", debugPayload);
            void apiDebugLog("instruction_page_data_failed", debugPayload);

            setInstructionStatus({
                isLoading: false,
                error: "Не удалось загрузить данные по инструкции",
            });
        }
    }, [auth, handleInvalidSessionError]);

    useEffect(() => {
        if (!auth.isAuthorized) {
            requestIdRef.current += 1;
            resetData();
            return;
        }

        if (auth.isAuthBypass) {
            requestIdRef.current += 1;
            applyDevData();
            return;
        }

        const currentRequestId = requestIdRef.current + 1;
        requestIdRef.current = currentRequestId;

        async function loadAppData() {
            await fetchMainPageData(currentRequestId);

            if (requestIdRef.current !== currentRequestId) {
                return;
            }

            void fetchCabinetData(currentRequestId);
            void fetchDevicesData(currentRequestId);
            void fetchTariffsData(currentRequestId);
            void fetchInstructionData(currentRequestId);
        }

        void loadAppData();
    }, [applyDevData, auth.isAuthBypass, auth.isAuthorized, fetchCabinetData, fetchDevicesData, fetchInstructionData, fetchMainPageData, fetchTariffsData, resetData]);

    const refreshMainPageData = useCallback(() => {
        if (auth.isAuthBypass) {
            applyDevData();
            return DEV_MAIN_PAGE_DATA;
        }

        return fetchMainPageData(requestIdRef.current);
    }, [applyDevData, auth.isAuthBypass, fetchMainPageData]);

    const refreshCabinetData = useCallback(() => {
        if (auth.isAuthBypass) {
            applyDevData();
            return DEV_CABINET_DATA;
        }

        return fetchCabinetData(requestIdRef.current);
    }, [applyDevData, auth.isAuthBypass, fetchCabinetData]);

    const refreshDevicesData = useCallback(() => {
        if (auth.isAuthBypass) {
            applyDevData();
            return DEV_DEVICES_DATA;
        }

        return fetchDevicesData(requestIdRef.current);
    }, [applyDevData, auth.isAuthBypass, fetchDevicesData]);

    const refreshTariffsData = useCallback(() => {
        if (auth.isAuthBypass) {
            applyDevData();
            return DEV_TARIFFS_DATA;
        }

        return fetchTariffsData(requestIdRef.current);
    }, [applyDevData, auth.isAuthBypass, fetchTariffsData]);

    const refreshInstructionData = useCallback(() => {
        if (auth.isAuthBypass) {
            applyDevData();
            return DEV_INSTRUCTION_DATA;
        }

        return fetchInstructionData(requestIdRef.current);
    }, [applyDevData, auth.isAuthBypass, fetchInstructionData]);

    const value = useMemo(() => ({
        mainPageData,
        mainPageStatus,
        cabinetData,
        cabinetStatus,
        devicesData,
        devicesStatus,
        tariffsData,
        tariffsStatus,
        instructionData,
        instructionStatus,
        refreshMainPageData,
        refreshCabinetData,
        refreshDevicesData,
        refreshTariffsData,
        refreshInstructionData,
        setCabinetData,
    }), [
        cabinetData,
        cabinetStatus,
        devicesData,
        devicesStatus,
        instructionData,
        instructionStatus,
        mainPageData,
        mainPageStatus,
        refreshCabinetData,
        refreshDevicesData,
        refreshInstructionData,
        refreshMainPageData,
        refreshTariffsData,
        tariffsData,
        tariffsStatus,
    ]);

    return <AppDataContext.Provider value={value}>{children}</AppDataContext.Provider>;
}

export function useAppData() {
    const context = useContext(AppDataContext);
    if (!context) {
        throw new Error("useAppData must be used inside AppDataProvider");
    }
    return context;
}
